import React, { useState, useMemo } from 'react';
import { API_BASE } from '@/lib/api';

/**
 * BrandLogo — single source of truth for brand iconography.
 *
 * Routes through `/api/proxy-logo?domain=…&name=…`. The backend uses HTTP
 * status (not heuristics) to determine whether a brand actually has a logo.
 * If the proxy returns 404, we render text initials.
 */
export default function BrandLogo({ brand, className = '' }) {
    const name = brand?.name || 'Brand';
    const domain = brand?.domain || '';
    const [failed, setFailed] = useState(!domain);

    const initials = useMemo(() => (
        name
            .replace(/[^\p{L}\p{N}\s]/gu, '')
            .split(/\s+/)
            .map((w) => w[0])
            .filter(Boolean)
            .join('')
            .slice(0, 3)
            .toUpperCase() || '·'
    ), [name]);

    if (failed) {
        return (
            <div className={`flex items-center justify-center bg-cream ${className}`} data-testid="brand-logo-fallback">
                <span className="font-serif text-2xl text-inorbit-blue tracking-wide">{initials}</span>
            </div>
        );
    }

    const src = `${API_BASE}/proxy-logo?domain=${encodeURIComponent(domain)}&name=${encodeURIComponent(name)}`;

    return (
        <img
            src={src}
            alt={name}
            className={className}
            loading="lazy"
            data-testid="brand-logo-img"
            onError={() => setFailed(true)}
        />
    );
}
